import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { GAService } from '@domgen/dgx-fe-ga';

import {
  consoleCodes,
  isProductAGadget,
  isProviderEligible,
} from '@domgen/dgx-util-general';
import { addDays, isBefore, isSameDay } from 'date-fns';
import { MMRClaimEvent } from '../../../containers/manage-my-repair/manage-my-repair.container';
import { TimeFormatPipe } from '../../../pipes/timeFormat.pipe';
import { ManageMyRepairService } from '../../../services/manage-my-repair.service';
import {
  AvailableClaimType,
  CallCostType,
  ManageMyRepairResponse,
  MMRClaimDetail,
  ServiceOptionTypeEnum,
} from '../../../types/manage-my-repair-types';
import {
  ClaimType,
  RepairAction,
  ServiceAction,
} from '../../../types/plan-detail.types';
import { floorcareCodes } from '../../floorcare-product-codes';
import { DisplayDetails } from '../claim-detail-container/claim-detail-container.component';
import { RepairTrackerComponent } from '../repair-tracker/repair-tracker.component';
import { FeatureFlagService, ServiceOptionTypes } from '@domgen/dgx-components';
import { FeedbackComponentConfig } from '../feedback/types';

@Component({
  selector: 'claims-athome-myaccount-manage-my-repair-active-claim-details',
  templateUrl: './active-claim-details.component.html',
  styleUrls: ['./active-claim-details.component.scss'],
})
export class ManageMyRepairActiveClaimDetailsComponent implements OnChanges {
  @ViewChild('repairTracker', { static: false })
  repairTrackerComponent: RepairTrackerComponent;

  @Input() claimType: ClaimType;

  @Input() claimDetails: MMRClaimDetail;

  @Input() fullClaimDetails: ManageMyRepairResponse;

  @Input() displayDetails: DisplayDetails;

  @Input() action: RepairAction | ServiceAction;

  @Input() nextAction: string;

  @Input() callCostType: CallCostType | undefined;

  @Input() lastUpdated: Date | undefined;

  @Output() rebookClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() cancelClaim: EventEmitter<MMRClaimEvent> =
    new EventEmitter<MMRClaimEvent>();

  @Output() trackClaim: EventEmitter<string> = new EventEmitter<string>();

  mmrLiveChatEnabledFlag = 'mmrLiveChatEnabled';

  nextSteps = {
    'Self Send': ['envelope', 'box', 'truck'],
    Courier: ['clock', 'box', 'envelope-circle-check'],
    'Drop off': ['note-sticky', 'location-dot', 'screwdriver-wrench'],
  };

  todaysDate = `${new Date().getFullYear()}-${
    new Date().getMonth() + 1 >= 10
      ? new Date().getMonth() + 1
      : `0${new Date().getMonth() + 1}`
  }-${
    new Date().getDate() > 9 ? new Date().getDate() : `0${new Date().getDate()}`
  }`;

  isEligibleProvider = false;

  get showCancelButton(): boolean {
    const isCourierAndTodayOrTomorrow =
      this.claimDetails.serviceOptionType === 'Courier' &&
      this.isSameDayOrTomorrow;

    const appointmentDateHasPassed =
      new Date(this.claimDetails.appointmentDateSelected) <
      new Date(this.todaysDate);

    const noAppointmentDate = !this.claimDetails.appointmentDateSelected;

    const isSelfFit = this.claimDetails.serviceOptionType === 'Self Fit';

    return (
      (noAppointmentDate ||
        !isCourierAndTodayOrTomorrow ||
        !appointmentDateHasPassed) &&
      !isSelfFit
    );
  }

  get engineerTitle(): 'ENGINEER_REPAIR' | 'ENGINEER_SERVICE' {
    return `ENGINEER_${this.claimType}`;
  }

  get isPaymentPanelFlagEnabled(): boolean {
    return this.featureFlagService.isFeatureFlagEnabled(
      'claims-2-0-orbit-payment'
    );
  }

  get isClaimPaymentRequired(): boolean {
    const matchedType = this.fullClaimDetails?.availableClaimTypes?.find(
      (ct: AvailableClaimType) =>
        ct.claimTypeID === this.claimDetails?.claimTypeId
    );
    return matchedType?.claimPaymentRequired ?? false;
  }

  get appointmentIsToday(): boolean {
    return this.todaysDate === this.claimDetails.appointmentDateSelected;
  }

  get isMMRFeedbackCaptureEnabled(): boolean {
    return this.featureFlagService.isFeatureFlagEnabled(
      'mmr-feedback-capture',
      'mmrFeedbackCapture'
    );
  }

  get nonEngineerOption(): boolean {
    const nonEngineerOptions: string[] = [
      ServiceOptionTypeEnum.Courier,
      ServiceOptionTypeEnum['Self Send'],
      ServiceOptionTypeEnum['Drop off'],
    ];

    return nonEngineerOptions.includes(this.claimDetails.serviceOptionType);
  }

  get formattedAppointmentSlot(): string {
    if (!this.claimDetails.appointmentSlotSelected) return undefined;
    const splitDate = this.claimDetails.appointmentSlotSelected.split(' - ');

    return `${this.timeFormatPipe.transform(
      splitDate[0]
    )} - ${this.timeFormatPipe.transform(splitDate[1])}`;
  }

  get formattedRepairerName(): string {
    let brandName = this.claimDetails?.repairer?.name;
    brandName = brandName.replace(/\bSkyline$/i, '').trim();
    return brandName
      .split(' ')
      .map((brandName) => {
        if (brandName.length === 2) {
          return brandName.toUpperCase();
        } else {
          return (
            brandName.charAt(0).toUpperCase() + brandName.slice(1).toLowerCase()
          );
        }
      })
      .join(' ');
  }

  get isConsole(): boolean {
    return Object.keys(consoleCodes).includes(this.productCode);
  }

  addressToShow: string[] = [];

  addressTitleKeyMap = {};

  feedbackConfig: FeedbackComponentConfig = {
    activeStage: 0,
    selectedValue: [],
    finishFormValue: 3,
    stages: {
      0: {
        title: {
          text: "We'd like to hear your feedback",
        },
        description: 'Have we made it easy to manage your booking?',
        descriptionIsMultipleLines: false,
        options: [
          {
            value: 'Yes',
            label: 'Yes',
            icon: 'thumbs-up',
          },
          {
            value: 'No',
            label: 'No',
            icon: 'thumbs-down',
          },
        ],
      },
      1: {
        activeWhenPreviousValueIs: 'No',
        title: {
          text: 'Please tell us why',
        },
        button: {
          text: 'Send your reason',
        },
        options: [
          {
            value: 'Information displayed isn’t right',
            label: 'Information displayed isn’t right',
          },
          {
            value: 'Phone number doesn’t work',
            label: 'Phone number doesn’t work',
          },
          {
            value: 'I can’t use my preferred contact method',
            label: 'I can’t use my preferred contact method',
          },
          {
            value: 'Important information is missing',
            label: 'Important information is missing',
          },
          {
            value: 'My problem isn’t listed',
            label: 'My problem isn’t listed',
          },
          {
            value: 'I’d prefer not to say',
            label: 'I’d prefer not to say',
          },
        ],
      },
      2: {
        activeWhenPreviousValueIs: 'My problem isn’t listed',
        title: {
          text: 'Tell us more about the problem',
        },
        description: [
          'Please note, this feedback is to improve our website only',
          'To speak to us about your repair, <a href="https://www.domesticandgeneral.com/content/contact-domestic-general" target="_blank" rel="noopener"> visit our contact us page</a>',
          'Be careful not to share personal details (like your phone number)',
        ],
        descriptionIsMultipleLines: true,
        button: {
          text: 'Send your reason',
        },
        freeTextInput: true,
        maxCharCount: 500,
      },
      3: {
        title: {
          text: 'Thanks for your feedback',
          icon: 'thumbs-up',
          classes: 'success',
        },
      },
    },
  };

  feedbackCaptureConfig: FeedbackComponentConfig = {
    activeStage: 0,
    selectedValue: [],
    finishFormValue: 2,
    stages: {
      0: {
        title: {
          text: 'Find what you were looking for?',
        },
        description: '',
        descriptionIsMultipleLines: false,
        options: [
          {
            value: 'Yes',
            label: 'Yes',
            icon: 'thumbs-up',
          },
          {
            value: 'No',
            label: 'No',
            icon: 'thumbs-down',
          },
        ],
      },
      1: {
        activeWhenPreviousValueIs: 'No',
        title: {
          text: 'How can we help?',
        },
        description: '',
        descriptionIsMultipleLines: false,
        button: {
          text: 'Submit',
        },
        freeTextInput: true,
        maxCharCount: 5000,
      },
      2: {
        title: {
          text: 'Thanks for letting us know.',
          icon: 'thumbs-up',
          classes: 'success',
        },
        success: true,
      },
    },
  };

  submittedTrackerSurvey: Record<string, boolean> =
    JSON.parse(localStorage.getItem('submittedTrackerSurvey')) || {};

  timeSlot: string | undefined;

  gadgetType: 'APPLE' | 'GADGET' | undefined;

  constructor(
    private timeFormatPipe: TimeFormatPipe,
    private readonly GaService: GAService,
    private mmrService: ManageMyRepairService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.claimDetails) {
      this.addressTitleKeyMap = {
        claimAddress:
          this.claimDetails.serviceOptionType === ServiceOptionTypes.SelfFit
            ? 'DELIVERY_ADDRESS'
            : 'ADDRESS',
        collectionAddress: this.isConsole
          ? 'COLLECTION_ADDRESS'
          : 'COLLECTION_AND_RETURN_ADDRESS',
        returnAddress: 'RETURN_ADDRESS',
        deliveryAddress: 'DELIVERY_ADDRESS',
        returnDeliveryAddress: 'RETURN_DELIVERY_ADDRESS',
      };
      // Do this to not include undefined addresses in the list
      this.addressToShow = this.setAddressDisplays().filter(
        (i) => this.claimDetails[i]
      );

      this.gadgetType = isProductAGadget(
        this.fullClaimDetails?.item?.code,
        this.fullClaimDetails?.item?.manufacturer
      );

      // Work around left in as Orbit don't provide a time slot when booking an
      // AM/PM slot
      this.claimDetails.appointmentHistory =
        this.claimDetails.appointmentHistory.map((item) => {
          if (['AM', 'PM'].includes(item.timeSlot)) {
            item.appointmentTime =
              item.timeSlot === 'AM' ? '8:00 - 13:00' : '12:00 - 18:00';
          }
          return item;
        });

      if (changes.claimDetails?.currentValue?.repairer?.name) {
        this.isEligibleProvider = isProviderEligible(
          changes.claimDetails.currentValue.repairer.name
        );
      }
    }
  }

  setAddressDisplays(): string[] {
    if (!this.nonEngineerOption) {
      return ['claimAddress'];
    }

    if (
      this.claimDetails.collectionAddress &&
      this.claimDetails.serviceOptionType === ServiceOptionTypeEnum.Courier
    ) {
      return ['collectionAddress'];
    }

    if (
      this.claimDetails.serviceOptionType === ServiceOptionTypeEnum.Courier &&
      !this.claimDetails.appointmentDateSelected
    ) {
      this.claimDetails['deliveryAddress'] = this.claimDetails['claimAddress'];
      return ['deliveryAddress'];
    }

    return ['returnAddress'];
  }

  get isVisitInThePast(): boolean {
    if (this.claimDetails.appointmentHistory.length === 0) {
      return true;
    }
    const latestApptDate = new Date(
      this.claimDetails.appointmentHistory[
        this.claimDetails.appointmentHistory.length - 1
      ].appointmentDate
    );
    const todaysDate = new Date();

    return isBefore(latestApptDate, todaysDate);
  }

  get isSameDayOrTomorrow() {
    const today = new Date();
    const tomorrow = addDays(today, 1);

    const { appointmentDateSelected } = this.claimDetails || {};

    if (!appointmentDateSelected) return false;
    return (
      isSameDay(today, new Date(appointmentDateSelected)) ||
      isSameDay(tomorrow, new Date(appointmentDateSelected))
    );
  }

  get isFloocareProduct(): boolean {
    return floorcareCodes.includes(this.fullClaimDetails?.item?.code);
  }

  get productCode(): string {
    return this.fullClaimDetails?.item?.code;
  }

  get groupTitleAndBody() {
    if (this.gadgetType) {
      const serviceOptionType = this.claimDetails.serviceOptionType
        .replace(/ /g, '_')
        .toUpperCase();

      const isCourier = serviceOptionType === 'COURIER';

      return {
        title: 'NEED_CHANGE_OR_CANCEL',
        body: `CHANGE_CANCEL_BODY_${serviceOptionType}`,
        subBody:
          this.isConsole && isCourier
            ? 'CHANGE_CANCEL_BODY_CONSOLE_PROVIDER'
            : isCourier
            ? `CHANGE_CANCEL_BODY_${serviceOptionType}_REPAIRER`
            : undefined,
      };
    }

    if (this.claimDetails.serviceOptionType === ServiceOptionTypes.SelfFit) {
      return {
        title: 'NEED_AN_UPDATE',
        body: 'SELF_FIT_DESC',
      };
    }

    if (this.isSameDayOrTomorrow) {
      return { title: 'NEED_CHANGE_OR_CANCEL', body: 'CHANGE_CANCEL_BODY' };
    }

    if (this.isFloocareProduct) {
      return {
        title: 'NEED_CHANGE_OR_CANCEL',
        body: 'FLOORCARE_CALL_REPAIRER',
      };
    }

    return { title: 'NEED_AN_UPDATE', body: 'UPDATE_BODY' };
  }

  get isHeatingPlan(): boolean {
    return this.fullClaimDetails?.planCategoryCode === 'H';
  }

  trackEngineerContact(eventLabel: string) {
    this.GaService.tag({
      event: 'genericGAEvent',
      eventCategory: 'MMR-page',
      eventAction: 'engineer details',
      eventLabel: eventLabel,
      pagename: 'claims-manage-my-repairs',
      category: 'claims-dgx',
      planNumber: this.fullClaimDetails?.planNumber,
      productName: `${this.fullClaimDetails?.item?.manufacturer?.toLowerCase()} ${this.fullClaimDetails?.item?.code_description?.toLowerCase()}`,
      productStatus: `${this.fullClaimDetails.planState.toLowerCase()}`,
      productModel: this.fullClaimDetails?.item?.model_number,
      DigitalID: this.mmrService.userData?.['custom:digital_id'],
      customerID: this.mmrService.userData?.sub,
    });
  }

  feedbackGAHandler = (
    feedbackConfig: FeedbackComponentConfig,
    isComplete: boolean
  ) => {
    console.log('feedback form completed:', isComplete);
    const { activeStage } = feedbackConfig;

    this.GaService.tag({
      event: 'genericGAEvent',
      clientName: 'DG My Account',
      pagename: 'claims-manage-my-repairs',
      eventCategory: 'manage-your-repair-feedback',
      eventAction: feedbackConfig.stages[activeStage].title.text.toLowerCase(),
      eventLabel: (
        feedbackConfig.selectedValue[activeStage] ??
        feedbackConfig.selectedValue[0] ??
        ''
      ).toLowerCase(),
      planNumber: this.fullClaimDetails.planNumber,
      productName: `${this.fullClaimDetails.item.manufacturer} ${this.fullClaimDetails.item.code_description}`,
      claimedBookingReference: this.claimDetails.id,
      claimStatus: this.claimDetails.claimStatus,
    });

    if (!isComplete) return;

    const updatedTrackerInfo = {
      ...this.submittedTrackerSurvey,
      [this.claimDetails.id]: true,
    };

    this.submitFeedback(feedbackConfig);

    localStorage.setItem(
      'submittedTrackerSurvey',
      JSON.stringify(updatedTrackerInfo)
    );
  };

  submitFeedback(feedbackConfig: FeedbackComponentConfig): void {
    const formattedSubmitFlow = {
      area: 'repair-tracker',
      questions: feedbackConfig.selectedValue.map((value, index) => {
        return {
          question:
            feedbackConfig.stages[index].description ||
            feedbackConfig.stages[index].title.text,
          answer: value,
        };
      }),
      metaData: {
        isWHP: this.claimDetails.bookingOEM === 'whirlpool',
        isRepairTrackerActive: this.repairTrackerComponent.fullTrackerEnabled,
        planNumber: this.fullClaimDetails?.planNumber,
        claimStatus: this.claimDetails?.claimStatus,
        productName: `${this.fullClaimDetails?.item?.manufacturer} ${this.fullClaimDetails?.item?.code_description}`,
      },
    };
    this.mmrService.submitFeedback(
      formattedSubmitFlow,
      this.fullClaimDetails?.planNumber,
      this.claimDetails?.claimAddress
    );
  }
}
